import {ChangeDetectorRef, Component, OnDestroy, OnInit, Input} from '@angular/core';
import {Subject} from 'rxjs';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtQueueService} from '../../services/gt-queue.service';
import {GtNotificationService} from '../../services/gt-notification.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {ServiceNumberUtil} from '../../models/service-number.util';
import {takeUntil} from 'rxjs/operators';
import {QueueAgentModel, QueueDataModel, QueueStatus, QueueStatusColor} from '../../models/queue-data.model';

@Component({
  selector: 'gt-queue',
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss']
})
export class QueueComponent implements OnInit, OnDestroy {
  @Input() login: string;
  @Input() oname: string;
  @Input() canEdit = true;
  loggedIn = false;
  queueData: QueueDataModel[] = [];

  private destroyed = new Subject<void>();

  constructor(
    private authenticationService: GtAuthenticationService,
    private queueService: GtQueueService,
    private directoryService: GtDirectoryService,
    private notificationService: GtNotificationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.login = this.login ? this.login : this.authenticationService.login;
    this.oname = this.oname ? this.oname : this.authenticationService.oname;

    this.queueService
      .getAgentState(this.oname, this.login)
      .pipe(takeUntil(this.destroyed))
      .subscribe(loggedIn => {
        this.loggedIn = loggedIn;
        this.changeDetectorRef.detectChanges();
      }, this.error);

    this.directoryService
      .getServiceNumberModels(this.oname, true)
      .pipe(takeUntil(this.destroyed))
      .subscribe(serviceNumberDataViewModels => {
        let queueData = [];

        // get queues of current agent
        serviceNumberDataViewModels.forEach(s => {
          if (s.queueData) {
            queueData = queueData.concat(ServiceNumberUtil.getQueuesWhichAgentIsMemberOf(s.queueData, this.login));
          }
        });

        this.queueData = this.sortQueues(queueData);
        this.changeDetectorRef.detectChanges();
      });
  }

  private sortQueues(queueData): QueueDataModel[] {
    return queueData.sort((a, b) => {
      if (a.serviceNumber < b.serviceNumber) {
        return -1;
      }
      if (a.serviceNumber > b.serviceNumber) {
        return 1;
      }
      return 0;
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private error = error => {
    this.notificationService.errorNotification.next(error);
  };

  updateQueueAgentState(queueAgent: QueueAgentModel, newState: boolean) {
    if (queueAgent.active === newState) {
      return;
    }
    this.queueService
      .editSkill(queueAgent.organization, queueAgent.login, {
        queueCid: -1, // Update all queues on this serviceNumber
        serviceNumber: queueAgent.serviceNumber,
        deactivated: !newState,
        wrapupTime: queueAgent.wrapupTime,
        noReplyTime: queueAgent.noReplyTime,
        skillLevel: queueAgent.skillLevel
      })
      .subscribe(() => {}, this.error);

    queueAgent.active = newState;
    this.updateQueueStatus();
  }

  sendLoginStateToBackend(newLoggedin: boolean) {
    if (this.loggedIn === newLoggedin) {
      return;
    }
    if (newLoggedin) {
      this.queueService.loginAgent(this.oname, this.login).subscribe(() => {}, this.error);
    } else {
      this.queueService.logoutAgent(this.oname, this.login).subscribe(() => {}, this.error);
    }
    this.loggedIn = newLoggedin;
    this.queueData.forEach(q => {
      const me = this.getMyQueueAgent(q);
      newLoggedin ? (me.state = 1) : (me.state = 0);
    });
    this.updateQueueStatus();
  }

  private updateQueueStatus() {
    this.queueData.forEach(q => {
      q.agentsAvailable = q.queueAgents.filter(qa => qa.active && qa.state === 1).length;
      q.agentsLoggedIn = q.queueAgents.filter(qa => qa.active && qa.state > 0).length;
    });
  }

  getMyQueueAgent(queue: QueueDataModel): QueueAgentModel {
    return queue.queueAgents.find(qa => qa.login === this.login);
  }

  getQueueStatusColor(queue: QueueDataModel): string {
    if (queue && queue.queueAgents.length > 0) {
      return queue.agentsAvailable > 0 ? QueueStatusColor.STATUS_GREEN : QueueStatusColor.STATUS_RED;
    }
    return QueueStatusColor.STATUS_GRAY as string;
  }

  getQueueAgentStatusColor(queueAgent: QueueAgentModel): string {
    return ServiceNumberUtil.getQueueAgentStatusColor(queueAgent);
  }

  getQueueStatus(queueAgent: QueueAgentModel): string {
    return ServiceNumberUtil.getQueueStatus(queueAgent);
  }
}
